import { useState, useMemo } from 'react'
import type { ColorState, TextConfig, ElementType } from '../../types/contrast'
import { evaluateContrast, DEFAULT_FOREGROUND, DEFAULT_BACKGROUND } from '../../utils/contrastUtils'
import ColorInput from './ColorInput'
import ColorPreview from './ColorPreview'
import ContrastResult from './ContrastResult'

export default function ContrastCheckerWidget() {
  const [foreground, setForeground] = useState<ColorState>(DEFAULT_FOREGROUND)
  const [background, setBackground] = useState<ColorState>(DEFAULT_BACKGROUND)
  const [textConfig, setTextConfig] = useState<TextConfig>({
    size: 'normal',
    weight: 'normal'
  })
  const [elementType, setElementType] = useState<ElementType>('text')

  // Calculate contrast evaluation when colors change
  const evaluation = useMemo(() => {
    if (foreground.isValid && background.isValid) {
      return evaluateContrast(foreground.rgb, background.rgb)
    }
    return null
  }, [foreground, background])

  // Swap colors handler
  const handleSwap = () => {
    setForeground(background)
    setBackground(foreground)
  }

  return (
    <div id="contrast-checker-section" className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 md:p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Color Contrast Checker
      </h2>

      {/* Color Inputs */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <ColorInput
          id="foreground"
          label="Text Color (Foreground)"
          color={foreground}
          onChange={setForeground}
        />
        <ColorInput
          id="background"
          label="Background Color"
          color={background}
          onChange={setBackground}
        />
      </div>

      {/* Live Preview */}
      <div className="mb-8">
        <ColorPreview
          foreground={foreground}
          background={background}
          onSwap={handleSwap}
        />
      </div>

      {/* Settings */}
      <div className="grid md:grid-cols-2 gap-6 mb-8 items-start">
        {/* Element Type Toggle */}
        <fieldset>
          <legend className="block text-sm font-medium text-gray-700 mb-3">
            What are you testing?
          </legend>
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              type="button"
              onClick={() => setElementType('text')}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                elementType === 'text'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              aria-pressed={elementType === 'text'}
            >
              Text Elements
            </button>
            <button
              type="button"
              onClick={() => setElementType('ui-component')}
              className={`flex-1 px-4 py-2 text-sm font-medium border-l border-gray-300 transition-colors ${
                elementType === 'ui-component'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              aria-pressed={elementType === 'ui-component'}
            >
              UI Components
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            {elementType === 'text'
              ? 'For body text, headings, and labels'
              : 'For icons, borders, buttons, and form controls (3:1 minimum)'}
          </p>
        </fieldset>

        {/* Text Size & Weight (only for text elements) */}
        {elementType === 'text' && (
          <fieldset>
            <legend className="block text-sm font-medium text-gray-700 mb-3">
              Text Size & Weight
            </legend>
            <div className="flex rounded-lg border border-gray-300 overflow-hidden">
              {/* Size Selection */}
              <select
                value={textConfig.size}
                onChange={(e) => setTextConfig({
                  ...textConfig,
                  size: e.target.value as 'normal' | 'large'
                })}
                aria-label="Text size"
                className="flex-1 px-3 py-2 text-sm border-0 focus:ring-2 focus:ring-purple-500 focus:ring-inset bg-white"
              >
                <option value="normal">Normal (&lt;18pt)</option>
                <option value="large">Large (18pt+)</option>
              </select>

              {/* Weight Selection */}
              <select
                value={textConfig.weight}
                onChange={(e) => setTextConfig({
                  ...textConfig,
                  weight: e.target.value as 'normal' | 'bold'
                })}
                aria-label="Font weight"
                className="flex-1 px-3 py-2 text-sm border-0 border-l border-gray-300 focus:ring-2 focus:ring-purple-500 focus:ring-inset bg-white"
              >
                <option value="normal">Normal (400)</option>
                <option value="bold">Bold (700)</option>
              </select>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Large text: 18pt (24px) or 14pt (18.67px) bold
            </p>
          </fieldset>
        )}
      </div>

      {/* Results */}
      {evaluation ? (
        <ContrastResult
          evaluation={evaluation}
          textConfig={textConfig}
          elementType={elementType}
          foreground={foreground}
          background={background}
          onApplySuggestion={setForeground}
        />
      ) : (
        <div className="text-center py-8 text-gray-500">
          Enter valid colors to see contrast results
        </div>
      )}
    </div>
  )
}
